import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Menu, X, Check, ChevronDown, UserPlus, Link as LinkIcon, Share2, DollarSign,
  Building2, GraduationCap, Users, Briefcase, Star, Megaphone, Info,
  CheckCircle2, Mail, FileText,
  Linkedin, Facebook, MailIcon,
} from "lucide-react";

type Plan = "starter" | "growth" | "agency-pro";

const planPrices = {
  starter: { monthly: 97, annual: 78 * 12 },
  growth: { monthly: 197, annual: 158 * 12 },
  "agency-pro": { monthly: 347, annual: 278 * 12 },
} as const;

function calculateCommissions(leads: number, plan: Plan) {
  const monthly = planPrices[plan].monthly * 0.20 * leads;
  const annual = monthly * 12;
  const annualUpgraded = planPrices[plan].annual * 0.20 * leads;
  return { monthly, annual, annualUpgraded };
}

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

// ---------- Nav ----------
function AffiliateNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = "text-sm font-medium text-white/80 hover:text-[#D4A843] transition";

  const scrollApply = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <nav
      className="sticky top-0 z-50 transition-shadow"
      style={{
        backgroundColor: "#0F1E35",
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.18)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/landing" className="text-white font-bold text-lg">
          <span style={{ color: "#D4A843" }}>LegacyGrowth</span> Engine
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <Link to="/landing" className={linkCls}>Home</Link>
          <Link to="/pricing" className={linkCls}>Pricing</Link>
          <Link to="/affiliate" className={linkCls} style={{ color: "#D4A843" }}>Affiliate</Link>
          <Link to="/login" className={linkCls}>Sign In</Link>
          <a
            href="#apply"
            onClick={scrollApply}
            className="px-5 py-2 rounded-lg font-semibold text-sm transition hover:brightness-110"
            style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
          >
            Apply to Partner
          </a>
        </div>
        <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden border-t border-white/10 px-6 py-4 space-y-3" style={{ backgroundColor: "#0F1E35" }}>
          <Link to="/landing" className="block text-white/80">Home</Link>
          <Link to="/pricing" className="block text-white/80">Pricing</Link>
          <Link to="/affiliate" className="block" style={{ color: "#D4A843" }}>Affiliate</Link>
          <Link to="/login" className="block text-white/80">Sign In</Link>
          <a href="#apply" onClick={scrollApply} className="block w-full text-center px-5 py-2 rounded-lg font-semibold" style={{ backgroundColor: "#D4A843", color: "#1F3864" }}>
            Apply to Partner
          </a>
        </div>
      )}
    </nav>
  );
}

// ---------- Footer ----------
function AffiliateFooter() {
  return (
    <footer style={{ backgroundColor: "#0A1628" }} className="text-white/70 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div>
          <Link to="/landing" className="text-white font-bold text-lg mb-3 inline-block hover:opacity-80">
            <span style={{ color: "#D4A843" }}>LegacyGrowth</span> Engine
          </Link>
          <p className="text-sm leading-relaxed">
            Lead capture, scoring, and referral outreach for home care agencies and group homes.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm">Product</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/landing" className="hover:text-[#D4A843]">Features</Link></li>
            <li><Link to="/pricing" className="hover:text-[#D4A843]">Pricing</Link></li>
            <li><Link to="/login" className="hover:text-[#D4A843]">Sign In</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-[#D4A843]">About</a></li>
            <li><a href="#" className="hover:text-[#D4A843]">Contact</a></li>
            <li><Link to="/affiliate" className="hover:text-[#D4A843]">Affiliate Program</Link></li>
            <li><Link to="/api-docs" className="hover:text-[#D4A843]">API Docs</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm">Connect</h4>
          <div className="flex gap-3">
            <a href="#" aria-label="LinkedIn" className="hover:text-[#D4A843]"><Linkedin size={18} /></a>
            <a href="#" aria-label="Facebook" className="hover:text-[#D4A843]"><Facebook size={18} /></a>
            <a href="mailto:hello@legacygrowthengine.com" aria-label="Email" className="hover:text-[#D4A843]"><MailIcon size={18} /></a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-white/10 text-xs text-white/50 flex flex-col md:flex-row gap-2 md:justify-between">
        <span>© {new Date().getFullYear()} H&amp;E Associates LLC. All rights reserved.</span>
        <span>HIPAA-Ready · BAA Available</span>
      </div>
    </footer>
  );
}

// ---------- Page ----------
export default function Affiliate() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [calculatorLeads, setCalculatorLeads] = useState(5);
  const [calculatorPlan, setCalculatorPlan] = useState<Plan>("growth");
  const [applicationStep, setApplicationStep] = useState<1 | 2>(1);
  const [applicationData, setApplicationData] = useState({
    name: "",
    email: "",
    agencyName: "",
    agencyType: "",
    plan: "",
    referralMethod: "",
    agreedToTerms: false,
  });
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);

  // Inter font
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Range input styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      input[type=range].lge-slider {
        -webkit-appearance: none; appearance: none;
        width: 100%; height: 8px; border-radius: 999px;
        background: #1F3864; outline: none;
      }
      input[type=range].lge-slider::-webkit-slider-thumb {
        -webkit-appearance: none; appearance: none;
        width: 24px; height: 24px; border-radius: 50%;
        background: #D4A843; cursor: pointer; border: 3px solid #FFFFFF;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      }
      input[type=range].lge-slider::-moz-range-thumb {
        width: 24px; height: 24px; border-radius: 50%;
        background: #D4A843; cursor: pointer; border: 3px solid #FFFFFF;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const scrollTo = (id: string) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const commissions = calculateCommissions(calculatorLeads, calculatorPlan);

  const step1Valid = !!(applicationData.name && applicationData.email && applicationData.agencyName && applicationData.agencyType);

  const handleSubmit = () => {
    if (!applicationData.agreedToTerms) return;
    setApplicationSubmitting(true);
    setTimeout(() => {
      setApplicationSubmitting(false);
      setApplicationSuccess(true);
    }, 1500);
  };

  const partnerCards = [
    { Icon: Building2, title: "Agency Owners & Operators", desc: "Already on LegacyGrowth Engine? Refer other agency owners you know — from your professional network, associations, or communities. Agency Pro subscribers get automatic access." },
    { Icon: GraduationCap, title: "Healthcare Consultants", desc: "If you consult with home care agencies or residential programs on compliance, operations, or growth, LegacyGrowth Engine is a natural addition to your client recommendations." },
    { Icon: Users, title: "Community Leaders & Educators", desc: "Running a Skool community, Facebook group, or training program for care business owners? Your audience is our audience. Earn on every member you refer." },
    { Icon: Briefcase, title: "Healthcare Attorneys & Accountants", desc: "Advisors who work with home care or group home operators can add real value by introducing LegacyGrowth Engine — and earn recurring income for every client who subscribes." },
    { Icon: Star, title: "Industry Association Members", desc: "Active in NAHC, ANCOR, or state-level home care or DD associations? Your credibility in these communities makes your referrals convert at a higher rate." },
    { Icon: Megaphone, title: "Content Creators & Podcasters", desc: "If you create content for the home care or residential services space — YouTube, podcast, newsletter, social — your audience is a perfect fit for LegacyGrowth Engine." },
  ];

  const terms = [
    ["Eligibility", "Available to Agency Pro subscribers only. Apply via the form below."],
    ["Commission Rate", "20% of the referred subscriber's net monthly fee, recurring."],
    ["Payout Schedule", "Paid on the 15th of every month for the previous month's confirmed earnings."],
    ["Minimum Payout", "$50 minimum balance required before payout is issued. Balances below $50 roll over."],
    ["Payout Method", "Direct deposit (US bank accounts) or PayPal. Payout method set in your affiliate dashboard."],
    ["Cookie Duration", "Your referral link tracks conversions for 90 days after the first click."],
    ["Eligible Referrals", "Any new LegacyGrowth Engine subscriber who was not previously a user and signs up via your unique link."],
    ["Termination", "H&E Associates LLC reserves the right to terminate affiliate access for violations of program terms, including fraudulent referrals or self-referrals."],
  ];

  const faqs = [
    { q: "Do I need to be a LegacyGrowth Engine subscriber to apply?", a: "Yes. The affiliate program is currently available to active LegacyGrowth Engine subscribers. Agency Pro subscribers are approved automatically. Growth and Starter plan subscribers are reviewed individually. Non-subscribers are not eligible at this time." },
    { q: "When do I start earning commissions?", a: "You start earning from the moment a referred agency's trial converts to a paid subscription. If a referral stays on the free trial, you do not earn until they upgrade. Once they are paying, you earn every month they remain active." },
    { q: "What if a referral cancels?", a: "If a referred subscriber cancels their plan, commissions from that subscriber stop at the end of their final paid billing period. Your commissions from all other active referrals continue unaffected." },
    { q: "Is there a limit to how many agencies I can refer?", a: "No. There is no cap on the number of referrals or the total commissions you can earn. Refer as many agencies as you like." },
    { q: "How long does my referral link track conversions?", a: "Your referral link has a 90-day cookie window. If someone clicks your link and signs up within 90 days, the conversion is attributed to you even if they did not sign up immediately." },
    { q: "Can I refer agencies outside of Virginia and Maryland?", a: "Yes. LegacyGrowth Engine is available nationwide. While our founding market is the DMV region, agencies and group home operators from any US state can subscribe and count as your referrals." },
    { q: "What if I refer an agency who is already a subscriber?", a: "Referrals are only eligible if the referred organization has never previously had a LegacyGrowth Engine account. Referring existing subscribers — including yourself — is not eligible and may result in account termination." },
    { q: "How do I get paid?", a: "On the 15th of every month we process payouts for the previous month's confirmed earnings. You can set up direct deposit or PayPal in your affiliate dashboard. The minimum payout threshold is $50 — balances below $50 roll over to the next month." },
  ];

  return (
    <div style={{ fontFamily: "Inter, sans-serif", backgroundColor: "#F8F9FB" }} className="min-h-screen">
      <AffiliateNav />

      {/* HERO */}
      <section
        style={{
          backgroundColor: "#0F1E35",
          backgroundImage: "radial-gradient(circle at 50% 0%, rgba(212,168,67,0.18), transparent 60%)",
        }}
        className="px-6 lg:px-10 pt-24 pb-20 text-center"
      >
        <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>
          REFERRAL PARTNER PROGRAM
        </p>
        <h1 className="text-white font-bold mt-4" style={{ fontSize: "clamp(34px,5.5vw,56px)", lineHeight: 1.2 }}>
          Refer Agencies. Earn Monthly.<br />Every Referral. Every Month.
        </h1>
        <p className="text-white/70 mt-5 max-w-[580px] mx-auto text-[19px] leading-relaxed">
          LegacyGrowth Engine pays you recurring monthly commissions for every home care agency or group home operator you refer — for as long as they remain a subscriber. No cap. No expiry.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {["20% Recurring Commission", "Paid Monthly", "No Referral Cap"].map((p) => (
            <span
              key={p}
              className="text-sm font-bold px-4 py-2 rounded-full"
              style={{
                backgroundColor: "rgba(212,168,67,0.15)",
                border: "1px solid #D4A843",
                color: "#D4A843",
              }}
            >
              {p}
            </span>
          ))}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={scrollTo("apply")}
            className="px-7 py-3.5 rounded-lg font-bold transition hover:brightness-110"
            style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
          >
            Apply to Become a Partner →
          </button>
          <button
            onClick={scrollTo("how-it-works")}
            className="px-7 py-3.5 rounded-lg font-bold border-2 border-white text-white transition hover:bg-white/10"
          >
            See How It Works
          </button>
        </div>
      </section>

      {/* CALCULATOR */}
      <section id="calculator" className="bg-white px-6 lg:px-10 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>EARNINGS CALCULATOR</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(28px,4vw,40px)" }}>
            See What You Could Earn
          </h2>
          <p className="text-gray-500 mt-3 text-base">
            Drag the slider to see your estimated monthly and annual earnings based on how many agencies you refer.
          </p>
        </div>

        <div
          className="max-w-3xl mx-auto mt-10 p-8 rounded-[14px] bg-white"
          style={{ border: "2px solid #E5E7EB", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
        >
          <div>
            <p className="text-sm text-gray-500">Referred agencies on:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(["starter", "growth", "agency-pro"] as Plan[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setCalculatorPlan(p)}
                  className="px-4 py-2 rounded-full text-sm font-semibold border-2 transition"
                  style={{
                    backgroundColor: calculatorPlan === p ? "#D4A843" : "#FFFFFF",
                    borderColor: calculatorPlan === p ? "#D4A843" : "#E5E7EB",
                    color: calculatorPlan === p ? "#1F3864" : "#6B7280",
                  }}
                >
                  {p === "starter" ? "Starter ($97)" : p === "growth" ? "Growth ($197)" : "Agency Pro ($347)"}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <label className="block text-base font-bold" style={{ color: "#1F3864" }}>
              Number of agencies referred:
            </label>
            <div className="text-center font-bold mt-3" style={{ color: "#D4A843", fontSize: 48 }}>
              {calculatorLeads} <span className="text-2xl">agencies</span>
            </div>
            <input
              type="range"
              min={1}
              max={50}
              step={1}
              value={calculatorLeads}
              onChange={(e) => setCalculatorLeads(parseInt(e.target.value))}
              className="lge-slider mt-3"
            />
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { label: "Your Monthly Commission", value: `${fmt(commissions.monthly)} / mo`, color: "#D4A843", sub: "20% of each subscriber's monthly fee" },
              { label: "Your Annual Commission", value: `${fmt(commissions.annual)} / yr`, color: "#1F3864", sub: "recurring — as long as they stay subscribed" },
              { label: "If All Upgrade to Annual", value: `${fmt(commissions.annualUpgraded)} / yr`, color: "#2E75B6", sub: "annual plan subscribers pay upfront" },
            ].map((tile) => (
              <div key={tile.label} className="p-5 rounded-xl text-center" style={{ backgroundColor: "#F8F9FB" }}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{tile.label}</p>
                <p className="font-bold mt-2" style={{ color: tile.color, fontSize: 28 }}>{tile.value}</p>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{tile.sub}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 text-center mt-6 leading-relaxed">
            Commissions are calculated on net revenue after any applicable taxes and payment processing fees. Annual plan commissions are paid monthly at the equivalent monthly rate.
          </p>

          <div className="text-center mt-6">
            <button
              onClick={scrollTo("apply")}
              className="px-6 py-3 rounded-lg font-bold transition hover:brightness-110"
              style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
            >
              Apply to Partner & Start Earning →
            </button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" style={{ backgroundColor: "#F8F9FB" }} className="px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>HOW IT WORKS</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(28px,4vw,40px)" }}>
            Simple. Transparent. Recurring.
          </h2>
        </div>

        <div className="max-w-5xl mx-auto mt-12 grid md:grid-cols-4 gap-8 relative">
          {[
            { Icon: UserPlus, title: "Apply & Get Approved", desc: "Fill out the short partner application below. We approve most Agency Pro subscribers within 1 business day." },
            { Icon: LinkIcon, title: "Get Your Unique Link", desc: "After approval, your affiliate dashboard generates a unique referral link and a personalized discount code for your referrals." },
            { Icon: Share2, title: "Share with Your Network", desc: "Share your link via email, social media, your community, or directly with agency owners and group home operators you know." },
            { Icon: DollarSign, title: "Earn Every Month", desc: "You earn 20% of every referred subscriber's monthly fee — paid to you every month for as long as they remain active." },
          ].map(({ Icon, title, desc }, i) => (
            <div key={title} className="text-center relative group">
              <div
                className="mx-auto rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: "#D4A843", width: 56, height: 56 }}
              >
                <Icon size={26} style={{ color: "#1F3864" }} />
              </div>
              {i < 3 && (
                <div
                  className="hidden md:block absolute top-7 left-[calc(50%+40px)] right-[calc(-50%+40px)]"
                  style={{ borderTop: "2px dashed #D4A843" }}
                />
              )}
              <h3 className="font-bold mt-4 text-base" style={{ color: "#1F3864" }}>{title}</h3>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div
          className="max-w-2xl mx-auto mt-10 p-5 rounded-xl flex items-start gap-3"
          style={{ backgroundColor: "#FFFBF0", border: "2px solid #D4A843" }}
        >
          <Info size={22} style={{ color: "#D4A843" }} className="shrink-0 mt-0.5" />
          <p className="text-[15px] font-bold" style={{ color: "#1F3864" }}>
            No cap on referrals. No expiry on commissions. Refer 1 agency or 100 — you earn on all of them, every month.
          </p>
        </div>
      </section>

      {/* COMMISSION STRUCTURE */}
      <section className="bg-white px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>COMMISSION STRUCTURE</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(28px,4vw,40px)" }}>
            What You Earn Per Referral
          </h2>
        </div>

        <div className="max-w-5xl mx-auto mt-10 grid md:grid-cols-3 gap-6">
          {[
            { name: "Starter", price: 97, monthly: 19.40, annual: 232.80, recommended: false },
            { name: "Growth", price: 197, monthly: 39.40, annual: 472.80, recommended: true },
            { name: "Agency Pro", price: 347, monthly: 69.40, annual: 832.80, recommended: false },
          ].map((p) => (
            <div
              key={p.name}
              className="relative p-7 rounded-[14px] text-center bg-white transition-transform duration-300 hover:-translate-y-1"
              style={{
                border: p.recommended ? "2px solid #D4A843" : "2px solid #E5E7EB",
                boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              }}
            >
              {p.recommended && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-md text-[10px] font-bold tracking-wider"
                  style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
                >
                  MOST REFERRED
                </div>
              )}
              <h3 className="text-xl font-bold" style={{ color: "#1F3864" }}>{p.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Subscriber pays ${p.price}/mo</p>
              <p className="font-bold mt-4" style={{ color: "#D4A843", fontSize: 48, lineHeight: 1 }}>20%</p>
              <p className="text-xs text-gray-500 mt-1">of their monthly fee</p>
              <p className="text-2xl font-bold mt-4" style={{ color: "#1F3864" }}>You earn ${p.monthly}/mo</p>
              <p className="text-sm text-gray-500">per subscriber</p>
              <p className="text-lg font-bold mt-2" style={{ color: "#D4A843" }}>${p.annual}/yr per subscriber</p>
            </div>
          ))}
        </div>

        <div
          className="max-w-5xl mx-auto mt-10 px-8 py-7 rounded-xl"
          style={{ backgroundColor: "#0F1E35" }}
        >
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: "20%", label: "Commission Rate" },
              { value: "Monthly", label: "Payout Frequency" },
              { value: "Unlimited", label: "Referral Cap" },
            ].map((s, i) => (
              <div key={s.label} className={`text-center ${i < 2 ? "border-r border-white/10" : ""}`}>
                <p className="font-bold" style={{ color: "#D4A843", fontSize: "clamp(24px,3.5vw,36px)" }}>{s.value}</p>
                <p className="text-[13px] text-white/60 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 mt-6 pt-5 text-center text-sm text-white/80 leading-relaxed">
            Commissions are paid via direct deposit or PayPal on the 15th of every month for the previous month's earnings. Minimum payout threshold: $50.
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section style={{ backgroundColor: "#F8F9FB" }} className="px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>WHO SHOULD APPLY</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(26px,3.5vw,38px)" }}>
            You Already Know the People We're Looking For
          </h2>
          <p className="text-gray-500 mt-3 max-w-[560px] mx-auto text-base leading-relaxed">
            The best affiliates are people who are already connected to the home care and residential services community. If you work in or around this industry, you are already sitting on a valuable referral network.
          </p>
        </div>

        <div className="max-w-5xl mx-auto mt-10 grid grid-cols-2 lg:grid-cols-3 gap-5">
          {partnerCards.map(({ Icon, title, desc }) => (
            <div
              key={title}
              className="p-6 rounded-xl bg-white transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
              style={{ border: "1px solid #E5E7EB", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
            >
              <Icon size={28} style={{ color: "#D4A843" }} />
              <h3 className="font-bold mt-3 text-base" style={{ color: "#1F3864" }}>{title}</h3>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AFFILIATE DASHBOARD PREVIEW */}
      <section className="grid md:grid-cols-2">
        <div style={{ backgroundColor: "#0F1E35" }} className="px-6 lg:px-10 py-16 flex items-center justify-center">
          <div
            className="w-full max-w-md rounded-2xl p-5"
            style={{ backgroundColor: "#162032", boxShadow: "0 0 60px rgba(212,168,67,0.15)" }}
          >
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <span className="ml-2 text-white text-[13px] font-bold">Affiliate Dashboard</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { v: "12", l: "Active Referrals" },
                { v: "$472.80", l: "This Month's Earnings" },
                { v: "$3,847", l: "Total Earned" },
                { v: "94%", l: "Referral Retention Rate" },
              ].map((k) => (
                <div key={k.l} className="rounded-lg p-3" style={{ backgroundColor: "#0F1E35" }}>
                  <p className="font-bold" style={{ color: "#D4A843", fontSize: 20 }}>{k.v}</p>
                  <p className="text-[11px] text-white/60 mt-0.5">{k.l}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg p-2" style={{ backgroundColor: "#0F1E35" }}>
              <span className="text-white/70 text-xs flex-1 truncate px-2">legacygrowthengine.com/ref/eileen-k</span>
              <button className="text-xs font-bold px-3 py-1.5 rounded" style={{ backgroundColor: "#D4A843", color: "#1F3864" }}>Copy</button>
            </div>
            <div className="mt-3 space-y-2">
              {[
                { name: "Sunrise Home Care", plan: "Growth", earn: "$39.40/mo", status: "Active", color: "#16A34A" },
                { name: "Crestview Residential", plan: "Agency Pro", earn: "$69.40/mo", status: "Active", color: "#16A34A" },
                { name: "Capital Care Partners", plan: "Starter", earn: "$19.40/mo", status: "Trial", color: "#D4A843" },
              ].map((r) => (
                <div key={r.name} className="flex items-center justify-between rounded-lg p-2.5 text-xs" style={{ backgroundColor: "#0F1E35" }}>
                  <span className="text-white font-medium truncate flex-1">{r.name}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold mx-2" style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#9AAFC8" }}>{r.plan}</span>
                  <span className="text-white/80 mr-2">{r.earn}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ color: r.color, backgroundColor: `${r.color}22` }}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white px-6 lg:px-10 py-16 flex items-center">
          <div>
            <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>YOUR AFFILIATE DASHBOARD</p>
            <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(26px,3.5vw,38px)" }}>
              Track Every Referral. See Every Dollar.
            </h2>
            <p className="text-gray-500 mt-4 text-base leading-relaxed">
              Your affiliate dashboard gives you a real-time view of every agency you've referred, their current plan, their subscription status, and your earnings — both this month and cumulative. No spreadsheets. No chasing invoices. Everything in one place.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                ["Unique Referral Link", "Share one link. Track every click and conversion."],
                ["Real-Time Commission Tracking", "See exactly what you've earned and when it will be paid."],
                ["Referral Status Monitoring", "Know when your referrals are in trial, active, or at risk of churning."],
                ["Monthly Payout Reports", "Download a full earnings report for every payout period."],
              ].map(([title, desc]) => (
                <li key={title} className="flex items-start gap-3">
                  <Check size={18} style={{ color: "#D4A843" }} className="mt-0.5 shrink-0" />
                  <span className="text-sm">
                    <span className="font-bold" style={{ color: "#1F3864" }}>{title}</span>
                    <span className="text-gray-500"> — {desc}</span>
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={scrollTo("apply")}
              className="mt-7 px-6 py-3 rounded-lg font-bold transition hover:brightness-110"
              style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
            >
              Apply to Partner →
            </button>
          </div>
        </div>
      </section>

      {/* TERMS SUMMARY */}
      <section className="bg-white px-6 lg:px-10 py-12">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>PROGRAM TERMS</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(24px,3vw,34px)" }}>
            The Rules, Plainly Written
          </h2>
          <div className="mt-7">
            {terms.map(([label, desc]) => (
              <div key={label} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 md:gap-6 py-4 border-b border-gray-200">
                <div className="font-bold text-sm" style={{ color: "#1F3864" }}>{label}:</div>
                <div className="text-sm text-gray-500 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-7 p-5 rounded-r-lg flex items-start gap-3" style={{ backgroundColor: "#EEF4FB", borderLeft: "4px solid #2E75B6" }}>
            <FileText size={20} style={{ color: "#2E75B6" }} className="shrink-0 mt-0.5" />
            <p className="text-sm" style={{ color: "#1F3864" }}>
              Full affiliate program terms and conditions are available upon approval. By applying, you agree to be bound by those terms. Contact <a href="mailto:affiliate@legacygrowthengine.com" className="underline font-semibold">affiliate@legacygrowthengine.com</a> with any questions.
            </p>
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" style={{ backgroundColor: "#F8F9FB" }} className="px-6 lg:px-10 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>BECOME A PARTNER</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(28px,4vw,40px)" }}>
            Apply to the LegacyGrowth Engine Partner Program
          </h2>
          <p className="text-gray-500 mt-3 text-base leading-relaxed">
            Applications are reviewed within 1 business day. Agency Pro subscribers are approved automatically. All other applicants are reviewed on a case-by-case basis.
          </p>
        </div>

        <div
          className="max-w-2xl mx-auto mt-10 bg-white rounded-[14px] p-8 md:p-10"
          style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
        >
          {!applicationSuccess ? (
            <>
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-3 mb-8">
                {[1, 2].map((n) => (
                  <div key={n} className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{
                        backgroundColor: applicationStep >= n ? "#D4A843" : "#FFFFFF",
                        color: applicationStep >= n ? "#1F3864" : "#9CA3AF",
                        border: applicationStep >= n ? "2px solid #D4A843" : "2px solid #E5E7EB",
                      }}
                    >
                      {n}
                    </div>
                    {n === 1 && <div className="w-12 h-0.5" style={{ backgroundColor: applicationStep >= 2 ? "#D4A843" : "#E5E7EB" }} />}
                  </div>
                ))}
              </div>

              {applicationStep === 1 && (
                <>
                  <h3 className="font-bold text-[17px]" style={{ color: "#1F3864" }}>
                    Step 1 of 2 — Tell us about yourself
                  </h3>
                  <div className="mt-5 space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Full Name</label>
                      <input
                        type="text"
                        value={applicationData.name}
                        onChange={(e) => setApplicationData({ ...applicationData, name: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:border-yellow-400 focus:outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        value={applicationData.email}
                        onChange={(e) => setApplicationData({ ...applicationData, email: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:border-yellow-400 focus:outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Organization Name</label>
                      <input
                        type="text"
                        placeholder="Your agency, practice, or company name"
                        value={applicationData.agencyName}
                        onChange={(e) => setApplicationData({ ...applicationData, agencyName: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:border-yellow-400 focus:outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Organization Type</label>
                      <select
                        value={applicationData.agencyType}
                        onChange={(e) => setApplicationData({ ...applicationData, agencyType: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:border-yellow-400 focus:outline-none transition bg-white"
                      >
                        <option value="">Select...</option>
                        <option>Home Care Agency</option>
                        <option>Group Home / Residential Program</option>
                        <option>Healthcare Consultant</option>
                        <option>Attorney or Accountant</option>
                        <option>Community Leader / Educator</option>
                        <option>Content Creator / Podcaster</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1.5">Current LegacyGrowth Engine Plan</label>
                      <select
                        value={applicationData.plan}
                        onChange={(e) => setApplicationData({ ...applicationData, plan: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:border-yellow-400 focus:outline-none transition bg-white"
                      >
                        <option value="">Select...</option>
                        <option>Agency Pro (auto-approved)</option>
                        <option>Growth Plan</option>
                        <option>Starter Plan</option>
                        <option>Not yet a subscriber</option>
                      </select>
                      {applicationData.plan === "Agency Pro (auto-approved)" && (
                        <p className="text-sm mt-2" style={{ color: "#16A34A" }}>
                          ✓ Agency Pro subscribers are automatically approved within 1 business day.
                        </p>
                      )}
                      {applicationData.plan === "Not yet a subscriber" && (
                        <p className="text-sm mt-2" style={{ color: "#B45309" }}>
                          You must be an active LegacyGrowth Engine subscriber to join the affiliate program.{" "}
                          <Link to="/pricing" className="font-semibold underline" style={{ color: "#D4A843" }}>View plans →</Link>
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    disabled={!step1Valid}
                    onClick={() => setApplicationStep(2)}
                    className="mt-7 w-full py-3.5 rounded-lg font-bold transition disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110"
                    style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
                  >
                    Continue →
                  </button>
                </>
              )}

              {applicationStep === 2 && (
                <>
                  <button
                    onClick={() => setApplicationStep(1)}
                    className="text-sm text-gray-500 hover:text-gray-700 mb-4"
                  >
                    ← Back
                  </button>
                  <h3 className="font-bold text-[17px]" style={{ color: "#1F3864" }}>
                    Step 2 of 2 — How will you refer agencies?
                  </h3>
                  <div className="mt-5">
                    <label className="block text-xs font-bold text-gray-500 mb-1.5">How do you plan to refer agencies?</label>
                    <select
                      value={applicationData.referralMethod}
                      onChange={(e) => setApplicationData({ ...applicationData, referralMethod: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:border-yellow-400 focus:outline-none transition bg-white"
                    >
                      <option value="">Select...</option>
                      <option>Word of mouth / personal network</option>
                      <option>Social media (LinkedIn, Facebook, Instagram, TikTok)</option>
                      <option>Email newsletter or list</option>
                      <option>Online community (Skool, Facebook Group, Slack)</option>
                      <option>Podcast or YouTube channel</option>
                      <option>Client recommendations (I'm a consultant/advisor)</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <label className="mt-6 flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applicationData.agreedToTerms}
                      onChange={(e) => setApplicationData({ ...applicationData, agreedToTerms: e.target.checked })}
                      className="mt-1 w-5 h-5 accent-yellow-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600 leading-relaxed">
                      I have read and agree to the{" "}
                      <a href="#" className="underline font-semibold" style={{ color: "#D4A843" }}>Affiliate Program Terms</a>.
                      {" "}I confirm that I will not refer myself, create fake accounts, or engage in any fraudulent referral activity. I understand that violations will result in immediate termination of my affiliate account and forfeiture of any unpaid commissions.
                    </span>
                  </label>
                  <button
                    disabled={!applicationData.agreedToTerms || applicationSubmitting}
                    onClick={handleSubmit}
                    className="mt-6 w-full py-3.5 rounded-lg font-bold transition disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110"
                    style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
                  >
                    {applicationSubmitting ? "Submitting..." : "Submit My Application"}
                  </button>
                </>
              )}
            </>
          ) : (
            <div className="text-center">
              <div className="inline-flex items-center justify-center" style={{ borderRadius: "50%", boxShadow: "0 0 0 12px rgba(212,168,67,0.1)" }}>
                <CheckCircle2 size={56} style={{ color: "#D4A843" }} />
              </div>
              <h3 className="font-bold mt-5 text-[28px]" style={{ color: "#1F3864" }}>Application Received!</h3>
              <p className="text-gray-500 mt-3 text-base leading-relaxed">
                {applicationData.plan === "Agency Pro (auto-approved)"
                  ? `Your application is being processed. Agency Pro subscribers are approved within 1 business day. You will receive an email at ${applicationData.email} with your affiliate dashboard link and unique referral link.`
                  : `We have received your application and will review it within 1 business day. You will receive an email at ${applicationData.email} with our decision and next steps.`}
              </p>
              <div className="mt-7 grid sm:grid-cols-3 gap-4">
                {[
                  { Icon: Mail, title: "Check Your Email", desc: "Look for an email from affiliate@legacygrowthengine.com within 1 business day." },
                  { Icon: LinkIcon, title: "Prepare Your Content", desc: "Think about how you'll share your referral link — social post, email, direct outreach." },
                  { Icon: DollarSign, title: "Start Earning", desc: "Once approved, every referral who subscribes starts generating recurring commissions for you." },
                ].map(({ Icon, title, desc }) => (
                  <div key={title} className="p-4 rounded-xl text-center" style={{ backgroundColor: "#F8F9FB" }}>
                    <Icon size={24} style={{ color: "#D4A843" }} className="mx-auto" />
                    <p className="font-bold text-sm mt-2" style={{ color: "#1F3864" }}>{title}</p>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/"
                className="inline-block mt-7 px-6 py-3 rounded-lg font-bold border-2 transition hover:bg-yellow-50"
                style={{ borderColor: "#D4A843", color: "#D4A843" }}
              >
                Go to My Dashboard →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ backgroundColor: "#0F1E35" }} className="px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>PARTNER STORIES</p>
          <h2 className="text-white font-bold mt-3" style={{ fontSize: "clamp(26px,3.5vw,36px)" }}>
            What Our Partners Are Saying
          </h2>
        </div>
        <div className="max-w-5xl mx-auto mt-10 grid md:grid-cols-3 gap-5">
          {[
            { quote: "I referred three agency owners from my consulting practice in the first month. I now earn over $350 a month in recurring commissions without any additional work. It is genuinely passive income.", name: "Veronica M.", role: "Healthcare Operations Consultant", loc: "Maryland" },
            { quote: "I run a home care business community on Skool. I shared my affiliate link in one post and had four signups within a week. The commission dashboard makes it easy to track everything.", name: "Darnell A.", role: "Community Founder", loc: "DMV Home Care Network" },
            { quote: "As an attorney who works with home care agencies, recommending LegacyGrowth Engine is a natural fit. My clients benefit and I earn a residual income stream. The application process was straightforward.", name: "Camille R., Esq.", role: "Healthcare Regulatory Attorney", loc: "Virginia" },
          ].map((t) => (
            <div key={t.name} className="p-6 rounded-xl" style={{ backgroundColor: "#162032" }}>
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={14} fill="#D4A843" stroke="#D4A843" />)}
              </div>
              <p className="text-white/90 italic mt-3 text-[15px] leading-relaxed">"{t.quote}"</p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-white font-bold text-sm">— {t.name}</p>
                <p className="text-white/60 text-xs mt-0.5">{t.role}, {t.loc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white px-6 lg:px-10 py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-semibold tracking-[2px] text-center" style={{ color: "#D4A843" }}>AFFILIATE FAQ</p>
          <h2 className="font-bold text-center mt-3" style={{ color: "#1F3864", fontSize: "clamp(24px,3vw,34px)" }}>
            Common Questions
          </h2>
          <div className="mt-8 space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-[15px]" style={{ color: "#1F3864" }}>{f.q}</span>
                  <ChevronDown
                    size={20}
                    className="transition-transform shrink-0"
                    style={{ color: "#D4A843", transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm leading-relaxed text-gray-600">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        style={{
          backgroundColor: "#0F1E35",
          backgroundImage: "radial-gradient(circle at 50% 0%, rgba(212,168,67,0.15), transparent 60%)",
        }}
        className="px-6 lg:px-10 py-20 text-center"
      >
        <h2 className="text-white font-bold" style={{ fontSize: "clamp(32px,5vw,48px)" }}>
          Your Network Is Worth More Than You Think.
        </h2>
        <p className="text-white/70 mt-4 max-w-[520px] mx-auto text-[17px] leading-relaxed">
          Every agency owner or group home operator you know is a potential recurring commission. Apply today and start building a passive income stream from relationships you already have.
        </p>
        <button
          onClick={scrollTo("apply")}
          className="mt-7 px-8 py-4 rounded-lg font-bold transition hover:brightness-110"
          style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
        >
          Apply to Partner Now →
        </button>
        <p className="text-white/50 text-sm mt-6">Agency Pro Required  ·  Approved in 1 Business Day  ·  20% Recurring Commission</p>
      </section>

      <AffiliateFooter />
    </div>
  );
}
